const QUICK_ADD_INTENT_DELAY = 120;
const quickAddDesktopQuery = window.matchMedia('(min-width: 1024px)');

const fetchQuickAddWrapper = (url) =>
  theme.cache.request(url.toString(), {
    read: async (response) => {
      const text = await response.text();
      const wrapper = theme.utils.domParser.parseFromString(text, 'text/html').querySelector('.product-quick-add-wrapper');
      if (!wrapper) throw new Error('Quick add markup not found');
      return wrapper.outerHTML;
    },
  });

const parseQuickAddWrapper = (html) => {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content.firstElementChild;
};

const getQuickAddUrl = (url, view = 'quick-add') => {
  if (!url?.includes('/products/')) return;

  const parsedUrl = url.startsWith('/') ? window.location.origin + url : url

  const newUrl = new URL(parsedUrl);
  const params = newUrl.searchParams;
  params.set('view', view);
  newUrl.search = params.toString();

  return newUrl;
}

const priceFromSnapshots = new WeakMap();

function snapshotPriceWrapper(wrapper) {
  const fromElement = wrapper?.querySelector('.price__from');
  if (!fromElement || priceFromSnapshots.has(wrapper)) return;

  const priceElement = wrapper.querySelector('.price__amount');
  const compareElement = wrapper.querySelector('.price__amount--compare');
  const compareWrapper = compareElement?.closest('.price__sale');

  priceFromSnapshots.set(wrapper, {
    priceText: priceElement?.textContent,
    compareText: compareElement?.textContent,
    onSale: wrapper.classList.contains('price--on-sale'),
    soldOut: wrapper.classList.contains('price--sold-out'),
    compareHidden: compareWrapper?.hidden,
  });
}

function restorePriceWrapper(wrapper) {
  const fromElement = wrapper?.querySelector('.price__from');
  if (!fromElement) return;

  fromElement.hidden = false;

  const snapshot = priceFromSnapshots.get(wrapper);
  if (!snapshot) return;

  const priceElement = wrapper.querySelector('.price__amount');
  const compareElement = wrapper.querySelector('.price__amount--compare');
  const compareWrapper = compareElement?.closest('.price__sale');

  if (priceElement && snapshot.priceText != null) priceElement.textContent = snapshot.priceText;
  if (compareElement && snapshot.compareText != null) compareElement.textContent = snapshot.compareText;
  wrapper.classList.toggle('price--on-sale', snapshot.onSale);
  wrapper.classList.toggle('price--sold-out', snapshot.soldOut);
  if (compareWrapper && snapshot.compareHidden != null) compareWrapper.hidden = snapshot.compareHidden;

  priceFromSnapshots.delete(wrapper);
}

if (!customElements.get('product-quick-add')) {
  class ProductQuickAdd extends HTMLElement {
    constructor() {
      super();

      this.bindOnOptionClick = this.onOptionClick.bind(this);
      this.bindOnSubmitHandler = this.onSubmitHandler.bind(this);
      this.bindOnNotifyClick = this.onNotifyClick.bind(this);
      this.bindExitClick = this.exitClick.bind(this);
      this._notifyAbortController = null;
    }

    connectedCallback() {
      this._notifyAbortController = new AbortController();
      this.setUp();
    }

    setUp() {
      this.options = Array.from(this.querySelectorAll('.product-quick-add__variant__option'));
      this.submit = this.querySelector('.product-quick-add__submit');
      this.notify = this.querySelector('.product-quick-add__notify');
      this.variantsJson = this.querySelector('.product-quick-add-json');
      this.variants = this.variantsJson ? JSON.parse(this.variantsJson.textContent) : null;
      this.notifyHandle = this.notify?.dataset.notifyHandle || null;
      this.form = this.querySelector('form');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.errorElement = this.querySelector('.product-quick-add__error');
      this.input = this.querySelector('input[name="id"]');
      this.modal = document.querySelector('product-quick-add-modal');

      if (!this.form || !this.input) return;

      this.updateOptions();

      this.form.addEventListener('submit', this.bindOnSubmitHandler);

      this.options.forEach(option => {
        option.addEventListener('click', this.bindOnOptionClick)
      })

      if (this.hasAttribute('hover-effect')) {
        this.dragCleanups = [];
        this.querySelectorAll('.product-quick-add__variant__options').forEach(row => {
          this.setUpDragScroll(row);
        });
      }

      if (!this.notifyHandle || !this.notify) return;

      theme.utils.restockNotify.onReady(() => this.setUpNotify());
    }

    setUpDragScroll(row) {
      let isDown = false;
      let dragged = false;
      let startX = 0;
      let startScrollLeft = 0;
      let pointerId = null;

      const onPointerDown = (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        event.stopPropagation();
        isDown = true;
        dragged = false;
        startX = event.clientX;
        startScrollLeft = row.scrollLeft;
        pointerId = event.pointerId;
      };

      const onPointerMove = (event) => {
        if (!isDown) return;
        const delta = event.clientX - startX;
        if (!dragged && Math.abs(delta) > 5) {
          dragged = true;
          row.classList.add('grabbing');
          row.setPointerCapture(pointerId);
        }
        if (dragged) row.scrollLeft = startScrollLeft - delta;
      };

      const onPointerUp = (event) => {
        isDown = false;
        row.classList.remove('grabbing');
        if (row.hasPointerCapture?.(event.pointerId)) {
          row.releasePointerCapture(event.pointerId);
        }
      };

      const onClickCapture = (event) => {
        if (!dragged) return;
        dragged = false;
        event.preventDefault();
        event.stopPropagation();
      };

      const onMouseDownStopPropagation = (event) => {
        event.stopPropagation();
      };

      const onTouchStartStopPropagation = (event) => {
        event.stopPropagation();
      };

      const onWheel = (event) => {
        if (row.scrollWidth <= row.clientWidth) return;
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        event.stopPropagation();
        event.preventDefault();
        row.scrollLeft += event.deltaX;
      };

      row.addEventListener('pointerdown', onPointerDown);
      row.addEventListener('pointermove', onPointerMove);
      row.addEventListener('pointerup', onPointerUp);
      row.addEventListener('pointercancel', onPointerUp);
      row.addEventListener('click', onClickCapture, true);
      row.addEventListener('mousedown', onMouseDownStopPropagation);
      row.addEventListener('touchstart', onTouchStartStopPropagation, { passive: true });
      row.addEventListener('wheel', onWheel, { passive: false });

      this.dragCleanups.push(() => {
        row.removeEventListener('pointerdown', onPointerDown);
        row.removeEventListener('pointermove', onPointerMove);
        row.removeEventListener('pointerup', onPointerUp);
        row.removeEventListener('pointercancel', onPointerUp);
        row.removeEventListener('click', onClickCapture, true);
        row.removeEventListener('mousedown', onMouseDownStopPropagation);
        row.removeEventListener('touchstart', onTouchStartStopPropagation);
        row.removeEventListener('wheel', onWheel);
      });
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.bindOnSubmitHandler);
      this.options?.forEach(option => {
        option.removeEventListener('click', this.bindOnOptionClick)
      })
      this.notify?.removeEventListener('click', this.bindOnNotifyClick);
      this.dragCleanups?.forEach(cleanup => cleanup());
      this.dragCleanups = [];
      this._notifyAbortController?.abort();
    }

    open(opener) {
      this.previousActiveElement = opener || document.activeElement;
      this.setAttribute('open', '');
      requestAnimationFrame(() => {
        document.documentElement.addEventListener('click', this.bindExitClick)
        if (opener) {
          opener.classList.remove('loading');
          opener.removeAttribute('aria-disabled');
          opener.removeAttribute('disabled');
          opener.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      })
    }

    close() {
      this.removeAttribute('open');
      document.documentElement.removeEventListener('click', this.bindExitClick)
      this.previousActiveElement?.focus();
      this.previousActiveElement = null;
      this.restoreFromPrice();
    }

    restoreFromPrice() {
      const card = this.getOriginatingCard();
      const priceWrappers = [...new Set([card?.querySelector('.price'), this.querySelector('.price')].filter(Boolean))];
      priceWrappers.forEach(wrapper => restorePriceWrapper(wrapper));
    }

    exitClick(event) {
      if (!event.target.closest('product-quick-add') && !event.target.closest('.product-card-swatches .swatch')) {
        this.close();
      }
    }

    onOptionClick(event) {
      const trigger = event.target.closest('.product-quick-add__variant__option');
      if (!trigger) {
        console.error('[Product Quick Add] No option trigger found.')
        return;
      }

      this.handleErrorMessage(null);

      this.submit.classList.add('loading');
      if (this.hasAttribute('direct-add')) {
        if (trigger.classList.contains('disabled')) {
          if (this.notifySetUp) {
            this.updateCurrentOption(trigger);
            this.updateVariant();
            requestAnimationFrame(() => {
              this.onNotifyClick();
              trigger.classList.remove('active');
            })
          }
        } else {
          this.classList.add('loading');
          this.updateCurrentOption(trigger);
          this.updateVariant();
          requestAnimationFrame(() => {
            this.submitForm();
          })
        }

      } else {
        if (trigger.classList.contains('disabled') && this.notifySetUp && this.hasAttribute('direct-notify')) {
          this.updateCurrentOption(trigger);
          this.updateOtherOptions(trigger)
          this.updateVariant();
          requestAnimationFrame(() => {
            this.onNotifyClick();
            trigger.classList.remove('active');
          })
        } else {
          this.changeOptions(trigger);
          if (this.currentVariant?.available) {
            this.setAttribute('user-interacted', '');
          }
        }

      }
    }

    changeOptions(trigger) {
      this.updateCurrentOption(trigger);
      this.updateOtherOptions(trigger)
      this.updateVariant();
    }

    updateCurrentOption(trigger) {
      const optionPosition = trigger.dataset.optionPosition || '1';
      const optionSiblings = this.options.filter(op => op.dataset.optionPosition == optionPosition);
      optionSiblings.forEach(op => {
        const isActive = op === trigger;
        op.classList.toggle('active', isActive);
        if (op.hasAttribute('aria-pressed')) {
          op.setAttribute('aria-pressed', isActive);
        }
      })

      this.updateOptions();
    }

    updateOtherOptions(trigger) {
      const optionPosition = trigger.dataset.optionPosition || '1';
      const otherOptions = this.options.filter(op => op.dataset.optionPosition != optionPosition);
      otherOptions.forEach(op => {
        const opPosition = op.dataset.optionPosition || '1';
        const opValue= op.dataset.value || null;
        const optionVariant = this.variants.find(variant => {
          let matchOption1 = this.option1 == null ? variant.option1 == null : variant.option1 === this.option1;
          let matchOption2 = this.option2 == null ? variant.option2 == null : variant.option2 === this.option2;
          let matchOption3 = this.option3 == null ? variant.option3 == null : variant.option3 === this.option3;
          if (opPosition === '1') matchOption1 = opValue == null ? variant.option1 == null : variant.option1 === opValue;
          if (opPosition === '2') matchOption2 = opValue == null ? variant.option2 == null : variant.option2 === opValue;
          if (opPosition === '3') matchOption3 = opValue == null ? variant.option3 == null : variant.option3 === opValue;

          return matchOption1 && matchOption2 && matchOption3;
        }) || {available: false, id: null};

        op.classList.toggle('disabled', !optionVariant.available);
      })
    }

    updateOptions() {
      this.option1 = this.options.find(op => op.dataset.optionPosition === '1' && op.classList.contains('active'))?.dataset.value;
      this.option2 = this.options.find(op => op.dataset.optionPosition === '2' && op.classList.contains('active'))?.dataset.value;
      this.option3 = this.options.find(op => op.dataset.optionPosition === '3' && op.classList.contains('active'))?.dataset.value;

      this.currentVariant = this.variants.find(variant => {
        const matchOption1 = this.option1 == null ? variant.option1 == null : variant.option1 === this.option1;
        const matchOption2 = this.option2 == null ? variant.option2 == null : variant.option2 === this.option2;
        const matchOption3 = this.option3 == null ? variant.option3 == null : variant.option3 === this.option3;
        return matchOption1 && matchOption2 && matchOption3;
      }) || null;
    }

    updateVariant() {
      this.submit.classList.remove('loading');

      if (this.currentVariant) {
        this.input.removeAttribute('aria-disabled');
        this.input.removeAttribute('disabled');
        this.input.value = this.currentVariant.id;
        this.submit.classList.remove('unavailable');
      } else {
        this.input.setAttribute('aria-disabled', true);
        this.input.setAttribute('disabled', true);
        this.submit.classList.add('unavailable');
      }

      this.updateCardPrice();

      this.updateSubmitButton(this.currentVariant?.available);
    }

    getOriginatingCard() {
      return this.closest('.product-card') || this.closest('product-quick-add-modal')?.opener?.closest('.product-card') || null;
    }

    updatePriceWrapper(wrapper, onSale) {
      snapshotPriceWrapper(wrapper);

      const priceElement = wrapper.querySelector('.price__amount');
      const compareElement = wrapper.querySelector('.price__amount--compare');
      const fromElement = wrapper.querySelector('.price__from');

      if (priceElement) priceElement.textContent = theme.utils.formatMoney(this.currentVariant.price);
      if (fromElement) fromElement.hidden = true;
      wrapper.classList.toggle('price--on-sale', onSale);
      wrapper.classList.toggle('price--sold-out', !this.currentVariant.available);

      if (compareElement) {
        const compareWrapper = compareElement.closest('.price__sale');
        if (onSale) compareElement.textContent = theme.utils.formatMoney(this.currentVariant.compare_at_price);
        if (compareWrapper) compareWrapper.hidden = !onSale;
      }
    }

    updateCardPrice() {
      if (!this.currentVariant) return;

      const card = this.getOriginatingCard();
      const priceWrappers = [...new Set([card?.querySelector('.price'), this.querySelector('.price')].filter(Boolean))];
      const hasFromPrice = priceWrappers.some(wrapper => wrapper.querySelector('.price__from'));

      if (!this.hasAttribute('price-varies') && !hasFromPrice) return;

      const onSale = this.currentVariant.compare_at_price > this.currentVariant.price;

      priceWrappers.forEach(wrapper => this.updatePriceWrapper(wrapper, onSale));

      const saleBadge = card?.querySelector('.price__badge-sale');
      if (saleBadge) {
        saleBadge.hidden = !onSale;

        if (onSale && saleBadge.dataset.showPercentage === 'true') {
          const percentage = Math.round(((this.currentVariant.compare_at_price - this.currentVariant.price) * 100) / this.currentVariant.compare_at_price);
          saleBadge.textContent = (theme.locales?.product?.onSalePercentage || '').replace('[percentage]', percentage);
        }
      }
    }

    updateSubmitButton(available) {
      this.handleButtonDisable(!available);
      if (available) this.submit.querySelector('span').textContent = theme.locales.product.addToCart;
      else this.submit.querySelector('span').textContent = theme.locales.product.soldOut
    }

    async submitForm() {
      if (this.submit.getAttribute('aria-disabled') === 'true' || this.submit.hasAttribute('disabled')) return;

      this.handleButtonDisable(true);
      this.submit.classList.add('loading');
      this.submit.querySelector('.loading__spinner')?.classList.remove('hidden');

      const variantId = this.input?.value;

      theme.cart.errors.clearProductForm(variantId);

      try {
        const response = await theme.cart.add({ id: variantId, quantity: 1 }, { openDrawer: true });

        if (!this.cart) {
          window.location = theme.routes.cartUrl;
          return;
        }

        this.error = false;
        theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, {
          source: 'product-form',
          productVariantId: variantId,
          cartData: response,
          skipFetch: true,
        });

        const quickAddModal = this.closest('product-quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => { this.cart.renderContents(response); });
            },
            { once: true }
          );
          quickAddModal.close();
        } else {
          this.cart.renderContents(response);
        }
      } catch (error) {
        this.handleErrorMessage(error.originalError?.description || theme.cart.errorMessage(error));

        const soldOutMessage = this.submit.querySelector('.sold-out-message');
        if (soldOutMessage) {
          this.handleButtonDisable(true);
          this.submit.querySelector('span')?.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
        }
      } finally {
        if (this.modal?.hasAttribute('open')) {
          this.modal.close();
        }
        this.submit.classList.remove('loading');
        if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) {
          this.handleButtonDisable(false);
        }
        this.submit.querySelector('.loading__spinner')?.classList.add('hidden');
        this.classList.remove('loading');
        if (this.hasAttribute('direct-add')) {
          this.options.forEach((op) => {
            if (op.tagName.toLowerCase() === 'button') {
              op.classList.remove('active');
              op.blur();
            }
          });
        }
      }
    }

    onSubmitHandler(event) {
      event.preventDefault();
      if (!this.validateForm()) return;
      this.submitForm();
    }

    validateForm() {
      const variantId = this.input?.value;

      if (!variantId || variantId === '' || variantId === 'undefined') {
        this.handleErrorMessage(theme.locales.product?.selectVariant);
        return false;
      }

      if (!this.currentVariant) {
        this.handleErrorMessage(theme.locales.product?.variantUnavailable);
        return false;
      }

      if (!this.currentVariant.available) {
        this.handleErrorMessage(theme.locales.product?.soldOut);
        return false;
      }

      return true;
    }

    handleErrorMessage(errorMessage = false) {
      if (!this.errorElement) return;

      this.errorElement.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorElement.textContent = errorMessage;
        this.scrollTo({ top: this.scrollHeight, behavior: 'smooth' });
      } else {
        this.errorElement.textContent = '';
      }
    }

    handleButtonDisable(disable) {
      if (disable) {
        this.submit.setAttribute('aria-disabled', true);
        this.submit.setAttribute('disabled', true);
      } else {
        this.submit.removeAttribute('aria-disabled');
        this.submit.removeAttribute('disabled');
      }
    }

    setUpNotify() {
      if (this.notify && this.notifyHandle) {
        this.notifySetUp = true;
        this.notify.removeAttribute('disabled')
        this.notify.removeAttribute('aria-disabled');
        this.notify.addEventListener('click', this.bindOnNotifyClick)
      }
    }
    async onNotifyClick() {
      if (!this.currentVariant?.id || !this.notifyHandle || this.notify.hasAttribute('disabled')) return;

      this.notify.setAttribute('aria-disabled', true);
      this.notify.setAttribute('disabled', true);
      this.notify.classList.add('loading');
      this.notify.querySelector('.loading__spinner')?.classList.remove('hidden');

      try {
        const payload = await theme.cache.request(`${window.Shopify.routes.root}products/${this.notifyHandle}.js`, {
          read: (response) => response.json(),
        });
        if (this._notifyAbortController?.signal.aborted) return;
        theme.utils.restockNotify.openModal(payload, this.currentVariant.id);
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      } finally {
        this.notify.removeAttribute('aria-disabled');
        this.notify.removeAttribute('disabled');
        this.notify.classList.remove('loading');
        this.notify.querySelector('.loading__spinner')?.classList.add('hidden');
      }
    }

    renderQuickAdd(url, target, id = this.id, formId = this.dataset.formId) {
      if (!url || !target || !id || !formId) return;
      const isOpen = this.hasAttribute('open')
      fetch(url)
        .then((response) => response.text())
        .then((response) => {
          const html = theme.utils.domParser.parseFromString(response, 'text/html')
          const source = html.querySelector('.product-quick-add-wrapper');
          if (source) {
            const sourceId = source.querySelector('product-quick-add').id;
            const sourceFormId = source.querySelector('product-quick-add').dataset.formId;
            if (isOpen) {
              source.querySelector('product-quick-add').setAttribute('open', '');
            }
            target.innerHTML = source.innerHTML.replaceAll(sourceId, id).replaceAll(sourceFormId, formId);
            if (isOpen) {
              target.querySelector('product-quick-add').open();
            }
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.classList.remove('loading');
        })
    }
  }

  customElements.define('product-quick-add', ProductQuickAdd);
}

if (!customElements.get('product-quick-add-opener')) {
  class ProductQuickAddOpener extends HTMLElement {
    constructor() {
      super();

      this.bindOnClick = this.onClick.bind(this);
      this.bindOnPointerEnter = this.onPointerEnter.bind(this);
      this.bindOnPointerLeave = this.onPointerLeave.bind(this);
      this.bindPrefetch = this.prefetch.bind(this);
      this.panelRequest = null;
      this.intentTimer = null;
      this.intentController = null;
    }

    connectedCallback() {
      this.button = this.querySelector('button');

      this.button.addEventListener('click', this.bindOnClick);
      this.setUpIntent();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.bindOnClick);
      this.intentController?.abort();
      this.intentController = null;
      clearTimeout(this.intentTimer);
    }

    get card() {
      return this.closest('.js-quick-add-scope');
    }

    get isModalMode() {
      return (this.hasAttribute('mobile-modal') && window.innerWidth < 1024) || (this.hasAttribute('desktop-modal') && window.innerWidth >= 1024);
    }

    setUpIntent() {
      const card = this.card;
      if (!this.hasAttribute('hover-effect') || !card) return;

      this.intentController = new AbortController();
      const { signal } = this.intentController;
      card.addEventListener('pointerenter', this.bindOnPointerEnter, { signal });
      card.addEventListener('pointerleave', this.bindOnPointerLeave, { signal });
      card.addEventListener('focusin', this.bindPrefetch, { signal });

      if (card.matches(':hover')) this.prefetch();
    }

    onPointerEnter(event) {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.intentTimer);
      this.intentTimer = setTimeout(this.bindPrefetch, QUICK_ADD_INTENT_DELAY);
    }

    onPointerLeave() {
      clearTimeout(this.intentTimer);
    }

    prefetch() {
      if (!quickAddDesktopQuery.matches) return;
      this.materialise().catch(() => {});
    }

    getPanel() {
      return this.card?.querySelector('product-quick-add:not([inert])') || null;
    }

    materialise() {
      const card = this.card;
      const existing = this.getPanel();
      if (existing) return Promise.resolve(existing);
      if (this.panelRequest) return this.panelRequest;

      const url = getQuickAddUrl(this.button.dataset.productUrl, 'quick-add-inline');
      if (!url || !card) return Promise.resolve(null);

      this.panelRequest = fetchQuickAddWrapper(url)
        .then((html) => {
          if (!this.isConnected) return null;
          const wrapper = parseQuickAddWrapper(html);
          const placeholder = card.querySelector('.product-quick-add-wrapper');
          if (placeholder) {
            placeholder.replaceWith(wrapper);
          } else {
            const info = card.querySelector('.product-card__info');
            if (info) info.after(wrapper);
            else card.append(wrapper);
          }
          return wrapper.querySelector('product-quick-add');
        })
        .finally(() => {
          this.panelRequest = null;
        });

      return this.panelRequest;
    }

    resetButton() {
      this.button.classList.remove('loading');
      this.button.removeAttribute('aria-disabled');
      this.button.removeAttribute('disabled');
      this.button.querySelector('.loading__spinner')?.classList.add('hidden');
    }

    goToProduct() {
      window.location.assign(this.button.dataset.productUrl);
    }

    async onClick(event) {
      this.button.classList.add('loading');
      this.button.setAttribute('aria-disabled', true);
      this.button.setAttribute('disabled', true);
      this.button.querySelector('.loading__spinner')?.classList.remove('hidden');

      this.modal = document.querySelector('product-quick-add-modal');

      if (this.isModalMode) {
        let productUrl = getQuickAddUrl(this.button.dataset.productUrl);
        if (this.hasAttribute('product')) {
          productUrl = window.location;
        }
        if (this.modal) this.modal.renderProduct(productUrl, this);
        else this.goToProduct();
        return;
      }

      try {
        const panel = await this.materialise();
        if (panel) {
          panel.open(this.button);
        } else if (this.isConnected) {
          this.goToProduct();
        }
      } catch (error) {
        console.error(error);
        this.goToProduct();
      }
    }
  }

  customElements.define('product-quick-add-opener', ProductQuickAddOpener);
}

if (!customElements.get('product-quick-add-modal')) {
  class ProductQuickAddModal extends HTMLElement {
    constructor() {
      super();
      this.bindExitClick = this.exitClick.bind(this);
      this.bindOnWindowResize = theme.utils.debounce(this.onWindowResize.bind(this), 200);
      this.bindOnKeyDown = this.onKeyDown.bind(this);
      this.bindOnCloseClick = this.onCloseClick.bind(this);
      this._dragAbortController = null;
    }

    connectedCallback() {
      this.content = this.querySelector('.product-quick-add-modal__content');
      this.addEventListener('click', this.bindOnCloseClick);
      window.addEventListener('resize', this.bindOnWindowResize);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.bindOnCloseClick);
      window.removeEventListener('resize', this.bindOnWindowResize);
      this._teardownDrag();
    }

    _setupDrag() {
      this._teardownDrag();

      const panel = this.querySelector('.product-quick-add');
      const handle = panel?.querySelector('[data-drawer-handle]');
      if (!panel || !handle) return;

      this._drag = theme.utils.createDragToClose({
        host: this,
        panel,
        onClose: () => this.close(),
      });

      this._dragAbortController = new AbortController();
      const signal = this._dragAbortController.signal;
      panel.addEventListener('pointerdown', this._drag.onDragStart, { signal });
      document.addEventListener('pointermove', this._drag.onDragMove, { signal });
      document.addEventListener('pointerup', this._drag.onDragEnd, { signal });
      document.addEventListener('pointercancel', this._drag.onDragEnd, { signal });
    }

    _teardownDrag() {
      this._dragAbortController?.abort();
      this._dragAbortController = null;
      this._drag?.reset();
      this._drag = null;
    }

    onCloseClick(event) {
      if (event.target.closest('.product-quick-add__close')) this.close();
    }

    open(restoreFocusTo) {
      this.previousActiveElement = restoreFocusTo || document.activeElement;
      this.setAttribute('open', '');
      requestAnimationFrame(() => {
        this.addEventListener('click', this.bindExitClick);
        document.addEventListener('keydown', this.bindOnKeyDown);
        this.trapFocus();
      })
    }

    close() {
      this.removeAttribute('open');
      this.removeEventListener('click', this.bindExitClick);
      document.removeEventListener('keydown', this.bindOnKeyDown);
      this.previousActiveElement?.focus();
      document.body.dispatchEvent(new CustomEvent('modalClosed', { bubbles: true }));
      this.restoreFromPrice();
    }

    restoreFromPrice() {
      restorePriceWrapper(this.opener?.closest('.product-card')?.querySelector('.price'));
    }

    trapFocus() {
      const focusableElements = this.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key === 'Tab') {
        const focusableElements = Array.from(this.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ));
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    exitClick(event) {
      if (!event.target.closest('product-quick-add')) {
        this.close();
      }
    }

    renderProduct(url, opener) {
      if (!url || !this.content) return;

      this.opener = opener;

      fetchQuickAddWrapper(url).then(
        (html) => {
          this.content.innerHTML = parseQuickAddWrapper(html).innerHTML;
          this._setupDrag();
          this.resetOpener();
          this.open(this.opener?.button || this.opener?.querySelector('button'));
        },
        (error) => {
          console.error(error);
          this.resetOpener();
          this.opener?.goToProduct?.();
        }
      );
    }

    resetOpener() {
      if (this.opener) {
        this.opener.classList.remove('loading');
        this.opener.removeAttribute('aria-disabled');
        this.opener.removeAttribute('disabled');
        this.opener.querySelector('.loading__spinner')?.classList.add('hidden');
      }
      const openerButton = this.opener?.button || this.opener?.querySelector('button');
      if (openerButton) {
        openerButton.classList.remove('loading');
        openerButton.removeAttribute('aria-disabled');
        openerButton.removeAttribute('disabled');
        openerButton.querySelector('.loading__spinner')?.classList.add('hidden');
      }
    }

    onWindowResize() {
      if (this.hasAttribute('open')) {
        if (!this.opener.hasAttribute('mobile-modal') && window.innerWidth < 1024) {
          this.close();
        }
        if (!this.opener.hasAttribute('desktop-modal') && window.innerWidth >= 1024) {
          this.close();
        }
      }
    }
  }

  customElements.define('product-quick-add-modal', ProductQuickAddModal);
}